import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function CheckoutDialog({ open, onOpenChange, requestedIndex = null, requestedPos = null, onSuccess }) {
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [order, setOrder] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setOrder(null);
    setError("");
    setQuantity(1);
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [open, requestedIndex]);

  const createOrder = async (qty) => {
    const { data } = await api.post("/orders", {
      quantity: qty,
      requested_index: requestedIndex,
    });
    setOrder(data);
    return data;
  };

  const handleDemoPay = async () => {
    setBusy(true);
    setError("");
    try {
      const o = order || (await createOrder(quantity));
      const { data } = await api.post(`/orders/${o.id}/demo-capture`);
      toast.success(t("checkout.success"));
      onOpenChange(false);
      onSuccess?.(data.indices);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const x = requestedPos ? requestedPos.x : null;
  const y = requestedPos ? requestedPos.y : null;
  const effectiveQty = requestedIndex != null ? 1 : quantity;
  const total = effectiveQty * (config?.pixel_price_usd ?? 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-border rounded-none sm:max-w-md max-h-[88vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">{t("checkout.title")}</DialogTitle>
        </DialogHeader>

        <div className="border border-border divide-y divide-border mt-1">
          {requestedIndex != null && (
            <div className="flex justify-between px-4 py-2.5">
              <span className="text-[10px] tracking-[0.3em] text-muted-foreground">{t("checkout.pixel").toUpperCase()}</span>
              <span className="font-mono text-golddark tabular-nums" data-testid="checkout-pixel-coords">
                [{x}, {y}]
              </span>
            </div>
          )}
          {requestedIndex == null && (
            <div className="px-4 py-2.5 flex items-center justify-between gap-4">
              <Label htmlFor="checkout-qty" className="text-[10px] tracking-[0.3em] text-muted-foreground">
                {t("checkout.quantity").toUpperCase()}
              </Label>
              <Input
                id="checkout-qty"
                type="number"
                min={1}
                max={100}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                data-testid="checkout-quantity-input"
                className="rounded-none bg-[#F3EFE9] border-border focus-visible:ring-gold w-24 h-9"
              />
            </div>
          )}
          <div className="flex justify-between px-4 py-2.5">
            <span className="text-[10px] tracking-[0.3em] text-muted-foreground">{t("checkout.total").toUpperCase()}</span>
            <span className="font-mono text-xl text-[#3C3735] tabular-nums" data-testid="checkout-total">
              ${total}
            </span>
          </div>
        </div>
        <p className="text-[9px] tracking-[0.2em] text-muted-foreground mt-2">{t("checkout.priceNote").toUpperCase()}</p>

        {error && (
          <p className="text-xs text-[#FF3333] mt-2" data-testid="checkout-error-message">
            {error}
          </p>
        )}

        <div className="mt-3">
          {config?.demo_mode ? (
            <>
              <Button
                onClick={handleDemoPay}
                disabled={busy}
                data-testid="checkout-demo-pay-btn"
                className="w-full rounded-none bg-[#7D8C77] text-[#FAF8F5] hover:bg-[#3C3735] text-xs tracking-[0.2em] h-12"
              >
                {busy ? t("checkout.processing").toUpperCase() : `${t("checkout.payDemo").toUpperCase()} — $${total}`}
              </Button>
              <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed" data-testid="checkout-demo-note">
                {t("checkout.demoNote")}
              </p>
            </>
          ) : config?.paypal_client_id ? (
            <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: "USD" }}>
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                forceReRender={[total, requestedIndex]}
                createOrder={async () => {
                  const o = order || (await createOrder(quantity));
                  const { data } = await api.post(`/orders/${o.id}/paypal-create`);
                  return data.paypal_order_id;
                }}
                onApprove={async (data) => {
                  try {
                    const o = order || (await createOrder(quantity));
                    const res = await api.post(`/orders/${o.id}/capture`, { paypal_order_id: data.orderID });
                    toast.success(t("checkout.success"));
                    onOpenChange(false);
                    onSuccess?.(res.data.indices);
                  } catch (e) {
                    setError(formatApiError(e));
                  }
                }}
                onError={() => setError(t("common.error"))}
              />
            </PayPalScriptProvider>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
